import type { BusinessSettings, Money, Quantity, Unit } from '@prody/shared';

/* ------------------------------------------------------------------ *
 * Formatting is tenant-scoped, never global.
 *
 * Currency, timezone, date format and tax presentation all come from
 * the active business — a Barcelona wholesaler and an Almaty one run
 * the same build. Nothing in this file reads the browser locale or
 * `new Date()` in local time; both are bugs waiting for the first
 * customer in another zone.
 *
 * Intl formatters are memoised because a table of 500 rows × 6 numeric
 * columns constructs 3000 of them per render otherwise, and
 * `Intl.NumberFormat` construction is genuinely slow.
 * ------------------------------------------------------------------ */

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

const numberFormat = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `n|${locale}|${JSON.stringify(options)}`;
  let f = cache.get(key) as Intl.NumberFormat | undefined;
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    cache.set(key, f);
  }
  return f;
};

const dateFormat = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `d|${locale}|${JSON.stringify(options)}`;
  let f = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    cache.set(key, f);
  }
  return f;
};

/** Minor units per major unit. Most currencies are 2; JPY is 0. */
const minorUnitsFor = (locale: string, currency: string): number => {
  const parts = numberFormat(locale, { style: 'currency', currency }).resolvedOptions();
  return parts.maximumFractionDigits ?? 2;
};

export interface FormatOptions {
  settings: BusinessSettings;
}

export interface MoneyFormatOptions {
  /** Drop the currency symbol — used in table columns where the header carries it. */
  bare?: boolean;
  /** Force a leading + on positives. Debt and adjustment columns need it. */
  signed?: boolean;
  /** Round to whole units. Dashboard tiles only; never a document. */
  compact?: boolean;
}

export const createFormatters = ({ settings }: FormatOptions) => {
  const locale = settings.formatLocale;
  const tz = settings.timezone;

  /* ---------------- Money ---------------- */

  const money = (m: Money | null | undefined, opts: MoneyFormatOptions = {}): string => {
    if (!m) return '—';
    const digits = minorUnitsFor(locale, m.currency);
    const value = m.minor / 10 ** digits;

    if (opts.compact) {
      return numberFormat(locale, {
        style: opts.bare ? 'decimal' : 'currency',
        currency: m.currency,
        notation: 'compact',
        maximumFractionDigits: 1,
        signDisplay: opts.signed ? 'exceptZero' : 'auto',
      }).format(value);
    }

    return numberFormat(locale, {
      style: opts.bare ? 'decimal' : 'currency',
      currency: m.currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: opts.signed ? 'exceptZero' : 'auto',
    }).format(value);
  };

  /** Just the symbol, for column headers: "Total (€)". */
  const currencySymbol = (currency = settings.currency): string =>
    numberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currency;

  /* ---------------- Quantity ---------------- */

  /**
   * Weight is rendered at the precision the tenant's scales actually
   * read to. Showing 12.4837 kg because that is what the float says is
   * how you lose an argument with a chef.
   */
  const quantity = (q: Quantity | null | undefined, opts: { bare?: boolean } = {}): string => {
    if (!q) return '—';
    const digits = decimalsForUnit(q.unit, settings);
    const n = numberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(q.value);
    return opts.bare ? n : `${n} ${unitLabel(q.unit)}`;
  };

  /* ---------------- Percent & rates ---------------- */

  const percentFromBps = (bps: number, digits = 1): string =>
    numberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(bps / 10_000);

  /* ---------------- Dates ----------------
     `date` takes a PlainDate (YYYY-MM-DD) and never touches a timezone:
     a delivery on the 4th is on the 4th everywhere.
     `dateTime` takes an Instant and renders it in the business zone. */

  const date = (
    plain: string | null | undefined,
    style: 'short' | 'medium' | 'weekday' = 'short',
  ): string => {
    if (!plain) return '—';
    const [y, m, d] = plain.split('-').map(Number);
    if (!y || !m || !d) return plain;
    // Noon UTC: far enough from either midnight that no zone shifts the day.
    const at = new Date(Date.UTC(y, m - 1, d, 12));
    const options: Intl.DateTimeFormatOptions =
      style === 'weekday'
        ? { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' }
        : style === 'medium'
          ? { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }
          : { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'UTC' };
    return dateFormat(locale, options).format(at);
  };

  const dateTime = (
    instant: string | null | undefined,
    style: 'time' | 'short' | 'full' = 'short',
  ): string => {
    if (!instant) return '—';
    const at = new Date(instant);
    if (Number.isNaN(at.getTime())) return '—';
    const options: Intl.DateTimeFormatOptions =
      style === 'time'
        ? { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }
        : style === 'full'
          ? {
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
            }
          : {
              day: '2-digit', month: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
            };
    return dateFormat(locale, options).format(at);
  };

  /** "in 2 h", "3 d ago". Used on delivery windows and thread timestamps. */
  const relative = (instant: string | null | undefined, now = Date.now()): string => {
    if (!instant) return '—';
    const then = Date.parse(instant);
    if (Number.isNaN(then)) return '—';
    const diffS = Math.round((then - now) / 1000);
    const abs = Math.abs(diffS);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    if (abs < 60) return rtf.format(diffS, 'second');
    if (abs < 3600) return rtf.format(Math.round(diffS / 60), 'minute');
    if (abs < 86_400) return rtf.format(Math.round(diffS / 3600), 'hour');
    if (abs < 2_592_000) return rtf.format(Math.round(diffS / 86_400), 'day');
    return rtf.format(Math.round(diffS / 2_592_000), 'month');
  };

  /** "07:00–11:00" — the delivery window, in business-local clock time. */
  const timeWindow = (w: { from: string; to: string } | null | undefined): string =>
    w ? `${w.from}–${w.to}` : '—';

  /**
   * The trading day for a given instant, honouring `dayStartsAt`.
   * A market wholesaler's Tuesday begins at 03:00, so an order taken
   * at 01:30 belongs to Monday's book.
   */
  const tradingDay = (instant: string | Date = new Date()): string => {
    const at = new Date(instant);
    const wall = zonedParts(at, tz);
    const [h = 0, min = 0] = settings.dayStartsAt.split(':').map(Number);
    const beforeCutoff = wall.hour * 60 + wall.minute < h * 60 + min;
    // Shift by whole days on the UTC calendar built from the zoned
    // wall clock — safe across DST because we never cross a boundary
    // with a duration, only with a calendar-day decrement.
    const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, 12);
    const shifted = new Date(asUtc - (beforeCutoff ? 86_400_000 : 0));
    return shifted.toISOString().slice(0, 10);
  };

  /* ---------------- Misc ---------------- */

  const number = (v: number, digits = 0): string =>
    numberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(v);

  /** Human list: "Mon, Wed and Fri" — locale-correct conjunctions. */
  const list = (items: string[]): string =>
    new Intl.ListFormat(locale, { style: 'short', type: 'conjunction' }).format(items);

  return {
    locale,
    timezone: tz,
    currency: settings.currency,
    money,
    currencySymbol,
    quantity,
    percentFromBps,
    date,
    dateTime,
    relative,
    timeWindow,
    tradingDay,
    number,
    list,
  };
};

export type Formatters = ReturnType<typeof createFormatters>;

/**
 * Wall-clock fields of an instant in a given IANA zone, with no date
 * library. `formatToParts` is the only way to do this correctly; every
 * offset-arithmetic shortcut breaks twice a year.
 */
export const zonedParts = (
  at: Date,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(at);
  const pick = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    // Some ICU builds emit hour 24 for midnight under hour12:false.
    hour: pick('hour') % 24,
    minute: pick('minute'),
  };
};

/* ------------------------------------------------------------------ *
 * Units
 * ------------------------------------------------------------------ */

const UNIT_LABEL: Record<Unit, string> = {
  pc: 'pc', box: 'box', crate: 'crt', pallet: 'plt',
  g: 'g', kg: 'kg', t: 't', lb: 'lb', oz: 'oz',
  ml: 'ml', l: 'l', hl: 'hl',
  mm: 'mm', cm: 'cm', m: 'm',
};

export const unitLabel = (unit: Unit): string => UNIT_LABEL[unit];

const decimalsForUnit = (unit: Unit, settings: BusinessSettings): number => {
  if (unit === 'kg') {
    // Scales at Mercabarna read to 10 g → two decimals; a tenant with
    // 1 g precision gets three.
    return settings.weightPrecisionG >= 100 ? 1 : settings.weightPrecisionG >= 10 ? 2 : 3;
  }
  if (unit === 'g' || unit === 'ml' || unit === 'mm') return 0;
  if (unit === 'pc' || unit === 'box' || unit === 'crate' || unit === 'pallet') return 0;
  return 2;
};
